import _ from 'lodash';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import AlertService from '@/shared/alert/alert.service';
import AccountService from '@/account/account.service';
import DirectusService from '@/core/directus/directus.service';
import { LOCAL_COUNTRY, ALL_COUNTRY_CODE, TLSCITIZEN_WEBAPP_URL, IS_FRANCE_CLIENT } from '@/constants';
import ResidenceCountryCMSModel from '@/visa-application/models/ResidenceCountryCMSModel';
import LanguageCode from '@/locale/language-code';
import KeywordsCMSModel, { Keywords } from '@/visa-application/models/KeywordsCMSModel';
import CountryCMSModel from '@/visa-application/models/CountryCMSModel';
import { getLocalStorage, defineCanonicalTag } from '@/shared/utils/utils';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';

@Component
export default class ApplierResidenceCountry extends Vue {
  public residenceCountrySelected = '';
  public countryRedirection = '';
  public cmsFields: ResidenceCountryCMSModel = new ResidenceCountryCMSModel();
  public popupLanguageAlertMsg = '';
  public countryCMSData: CountryCMSModel[] = [];
  public confirm: Function;
  public showPopup = false;
  public showAlertPopup = false;
  public keywords: Partial<Keywords> = {};
  private currentLanguage: LanguageCode = this.$store.getters.currentLanguage;
  public swiperOption = {};
  public residenceCountryCodeFromLocalStorage = '';
  public issuerFromLocalStorage = '';
  public localStorageIssuerName = '';
  public localStorageCountryName = '';
  public loginError = false;
  public countryFromLocalStorageIsOpen = false;
  public issuerFromLocalStorageIsOpen = false;
  public showOpenIssuerHomePagePopup = false;
  public showLoginErrorPopup = false;

  @Inject('directusService') private directusService: () => DirectusService;
  @Inject('alertService') private alertService: () => AlertService;
  @Inject('accountService') private accountService: () => AccountService;

  public created(): void {
    this.residenceCountryCodeFromLocalStorage = getLocalStorage('residenceCountryCode');
    this.issuerFromLocalStorage = getLocalStorage('issuer');
    this.$store.commit('client', LOCAL_COUNTRY);
    this.$store.commit('residenceCountryCode', ALL_COUNTRY_CODE);

    defineCanonicalTag();
  }

  public mounted(): void {
    if (IS_FRANCE_CLIENT && TLSCITIZEN_WEBAPP_URL) {
      location.href = TLSCITIZEN_WEBAPP_URL;
    }
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  @Watch('$store.getters.currentLanguage', { immediate: true })
  public onCurrentLanguageChanged(language: LanguageCode): void {
    this.currentLanguage = language;
    this.getContent(language);
    this.getCountryFromCMS(language);
  }

  public submit(): void {
    // if select country,get availble language, show confirm popup
    if (this.residenceCountrySelected) {
      const filter = {
        code: {
          eq: this.residenceCountrySelected
        }
      };
      const availableLanguage = [];
      this.directusService()
        .getCollection<CountryCMSModel>(CountryCMSModel, 'country', ['*.*.*.*'], filter)
        .then((result: CountryCMSModel[]) => {
          this.countryRedirection = _.get(result[0], 'country_redirection');
          result[0].availableLanguages.forEach(elem => {
            availableLanguage.push(elem.code);
          });
          // if current language not in this country,show language text in popup
          if (availableLanguage.includes(this.currentLanguage)) {
            this.popupLanguageAlertMsg = '';
          } else {
            this.popupLanguageAlertMsg = this.cmsFields.popupLanguageAlertMsg;
          }
          this.showPopup = true;
        })
        .catch(error => console.error(error));
    } else {
      // if not select country, show select country popup
      this.showAlertPopup = true;
    }
  }

  public cancelCountryChoice(): void {
    this.showPopup = false;
  }

  public confirmCountry(): void {
    this.cancelCountryChoice();
    this.$router.push({
      name: 'ApplierApplicationCenter',
      params: {
        residenceCountryCode: _.get(this.countryRedirection, 'length') === 2 ? this.countryRedirection : this.residenceCountrySelected
      }
    });
  }

  public getContent(currentLanguage: LanguageCode) {
    this.directusService()
      .getCollection<ResidenceCountryCMSModel>(ResidenceCountryCMSModel, 'residence_country', ['*.*'], undefined, currentLanguage)
      .then((response: ResidenceCountryCMSModel[]) => {
        this.cmsFields = response[0];
        this.$store.commit('metaInfo', this.cmsFields);
        // 设置轮播时间
        // Set the rotation time
        if (Number(this.cmsFields.swipeAutoplayTime)) {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: Number(this.cmsFields.swipeAutoplayTime) * 1000,
              disableOnInteraction: false
            }
          };
        } else {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: 10000,
              disableOnInteraction: false
            }
          };
        }
      })
      .catch(error => console.error(error));
  }

  public confirmChoice(): void {
    this.showAlertPopup = false;
  }

  public cancelChoice(): void {
    this.showAlertPopup = false;
  }

  public getCountryFromCMS(currentLanguage: LanguageCode) {
    this.directusService()
      .getCollection<CountryCMSModel>(CountryCMSModel, 'country', ['*', 'translation.*'], undefined, currentLanguage)
      .then((response: CountryCMSModel[]) => {
        response = response.filter(item => item.code !== ALL_COUNTRY_CODE);
        const countryIndex = _.findIndex(response, country => country.code === this.residenceCountryCodeFromLocalStorage);
        this.countryFromLocalStorageIsOpen = countryIndex !== -1;
        this.loginError = this.$route.fullPath.endsWith('/login?error');
        if (
          this.loginError &&
          this.countryFromLocalStorageIsOpen &&
          this.issuerFromLocalStorage.endsWith(LOCAL_COUNTRY) &&
          this.issuerFromLocalStorage.startsWith(this.residenceCountryCodeFromLocalStorage)
        ) {
          this.localStorageCountryName = response[countryIndex].country_name_translation
            ? response[countryIndex].country_name_translation
            : this.residenceCountryCodeFromLocalStorage;
          const centerFilter = {
            'country.code': {
              eq: this.residenceCountryCodeFromLocalStorage
            }
          };
          this.directusService()
            .getCollection<ApplicationCenterCMSModel>(
              ApplicationCenterCMSModel,
              'application_centers',
              ['*', 'translation.*'],
              centerFilter,
              this.currentLanguage
            )
            .then(async (result: ApplicationCenterCMSModel[]) => {
              const issuerIndex = _.findIndex(result, center => center.code === this.issuerFromLocalStorage);
              this.issuerFromLocalStorageIsOpen = issuerIndex !== -1;
              if (this.issuerFromLocalStorageIsOpen) {
                this.localStorageIssuerName = result[issuerIndex].name_translation
                  ? result[issuerIndex].name_translation
                  : this.issuerFromLocalStorage;
                this.showOpenIssuerHomePagePopup = true;
              } else {
                this.showLoginErrorPopup = true;
              }
            });
        } else if (this.loginError) {
          this.showLoginErrorPopup = true;
        }
        response.forEach(country => {
          if (!country.country_name_translation) {
            country.country_name_translation = country.name;
          }
        });
        this.countryCMSData = _.sortBy(response, 'country_name_translation');
      })
      .catch(error => console.error(error));
  }

  public gotoIssuerHomePage() {
    this.$router.push({
      name: 'ApplicationCenterHome',
      params: {
        residenceCountryCode: this.residenceCountryCodeFromLocalStorage,
        issuer: this.issuerFromLocalStorage
      }
    });
  }
}
